import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Users } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function TeamAccept() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState(token ? "idle" : "fail");
  const [error, setError] = useState(token ? "" : "Missing invite token.");
  const [info, setInfo] = useState(null);
  const submittedRef = useRef(false);

  // No server-side "validate token" endpoint — we just let the user hit submit and
  // surface the 400/404 if stale.

  const submit = async (e) => {
    e.preventDefault();
    if (submittedRef.current) return;
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    submittedRef.current = true;
    setSubmitting(true); setError("");
    try {
      const { data } = await api.post("/team/accept", { token, password });
      setInfo(data); setState("ok");
      toast.success("Account created — please sign in.");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      submittedRef.current = false;
      setError(formatApiError(err?.response?.data?.detail) || "Could not accept invite.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary text-primary-foreground grid place-items-center p-6" data-testid="team-accept-page">
      <div className="max-w-md w-full bg-card text-card-foreground rounded-[10px] p-8 border border-border">
        {state === "ok" ? (
          <div className="flex flex-col items-center text-center gap-3" data-testid="team-accept-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
            <div className="font-extrabold text-lg">Welcome to {info?.company_name}</div>
            <div className="text-sm text-muted-foreground">You can now sign in with <span className="mono font-semibold">{info?.email}</span>.</div>
            <Link to="/login" className="text-accent font-semibold underline" data-testid="team-accept-login-link">Go to sign in</Link>
          </div>
        ) : state === "fail" ? (
          <div className="flex flex-col items-center text-center gap-3" data-testid="team-accept-fail">
            <XCircle className="h-10 w-10 text-destructive" />
            <div className="font-extrabold text-lg">Invite link invalid</div>
            <div className="text-sm text-muted-foreground">{error}</div>
            <div className="text-xs text-muted-foreground">Ask the company owner to resend the invite.</div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="team-accept-form">
            <div className="flex items-center gap-2 text-accent">
              <Users className="h-8 w-8" />
              <span className="mono text-[11px] uppercase tracking-[0.25em]">// Team invite</span>
            </div>
            <h1 className="text-2xl font-black tracking-tight">Set your password</h1>
            <p className="text-sm text-muted-foreground">Choose a password to accept the invite. You can change it later in Settings.</p>
            <div>
              <Label htmlFor="pw">Password</Label>
              <Input id="pw" type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="team-accept-password" />
            </div>
            <div>
              <Label htmlFor="cf">Confirm password</Label>
              <Input id="cf" type="password" minLength={8} required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="team-accept-confirm" />
            </div>
            {error && <div className="text-sm text-destructive" data-testid="team-accept-error">{error}</div>}
            <Button type="submit" disabled={submitting} className="btn-cta w-full" data-testid="team-accept-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Accept invite
            </Button>
            <p className="text-[11px] mono uppercase tracking-[0.15em] text-muted-foreground text-center">
              Already have access? <Link to="/login" className="text-accent underline">Sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
